import { skipToken } from '@reduxjs/toolkit/query';
import { Button, Result, Skeleton } from 'antd';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGetClientLeadInfoQuery } from '@app/api/Client/api';
import { LeadCard } from '@app/components/LeadCard';
import { useUserPrint } from '@app/hooks';

import { getPositiveNumberParam } from '../utils';

import type { FC } from 'react';

export const ClientLeadCardPage: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const leadId = useMemo(
    () => getPositiveNumberParam(searchParams.get('leadId')),
    [searchParams]
  );
  const clientId = useMemo(
    () => getPositiveNumberParam(searchParams.get('id')),
    [searchParams]
  );

  const queryArgs = useMemo(() => {
    if (leadId !== null || clientId === null || ActiveBranchId === null) {
      return skipToken;
    }

    return {
      clientId,
      ActiveBranchId,
    };
  }, [ActiveBranchId, clientId, leadId]);

  const {
    data: clientLeadInfo,
    isFetching: isClientLeadInfoFetching,
    isLoading: isClientLeadInfoLoading,
    isUninitialized: isClientLeadInfoUninitialized,
    isError: isClientLeadInfoError,
  } = useGetClientLeadInfoQuery(queryArgs);

  const resolvedClientLeadId =
    clientLeadInfo?.LeadId && clientLeadInfo.LeadId > 0
      ? clientLeadInfo.LeadId
      : null;

  const resolvedLeadId = leadId ?? resolvedClientLeadId;
  const hasResolvableClient = leadId !== null || clientId !== null;
  const isWaitingForBranch =
    leadId === null && clientId !== null && ActiveBranchId === null;
  const isLoading =
    isWaitingForBranch ||
    isClientLeadInfoLoading ||
    isClientLeadInfoFetching ||
    (clientId !== null && isClientLeadInfoUninitialized && leadId === null);

  if (!hasResolvableClient) {
    return (
      <Result
        status="404"
        title="Карточка лида не найдена"
        subTitle="Не удалось определить клиента или лида для открытия карточки."
        extra={
          <Button type="primary" onClick={() => navigate(-1)}>
            Вернуться назад
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 12 }} />;
  }

  if (resolvedLeadId === null || isClientLeadInfoError) {
    return (
      <Result
        status="404"
        title="Карточка лида не найдена"
        subTitle="Лид для выбранного клиента не существует или был удален."
        extra={
          <Button type="primary" onClick={() => navigate(-1)}>
            Вернуться назад
          </Button>
        }
      />
    );
  }

  return (
    <LeadCard
      key={resolvedLeadId}
      leadId={resolvedLeadId}
      showLeadList={false}
      showGoToLeadAction
    />
  );
};
